/**
 * Ovoz: avval serverdagi audio (`/v1/words/{id}/audio`, Gemini TTS, keshlanadi) — Telegram Android WebView'da
 * `speechSynthesis` umuman ishlamaydi, iOS'da ovozlar kech yuklanadi. Matn bazadagi so'z/misol bo'lmasa yoki server
 * javob bermasa — brauzer `speechSynthesis` — faqat o'rganilayotgan tildagi ovoz. Rus ovozi bo'lmasa inglizcha o'qimaydi,
 * bir marta ko'rsatma (noVoice) ko'rsatadi. Mikrofon: `recorder.ts` (WAV) → API `/v1/speech/check` (Gemini).
 */
import { BYID, LANG, LN, W } from '../data';
import { API_URL } from './api';

let voice: SpeechSynthesisVoice | null | undefined = null; // undefined = ovozlar hali yuklanmagan
export const canSpeak = () => { try { return 'speechSynthesis' in window; } catch { return false; } };

function pickVoice(): SpeechSynthesisVoice | null | undefined {
  const vs = speechSynthesis.getVoices();
  if (!vs.length) return undefined;
  const m = vs.filter((v) => v.lang.toLowerCase().replace('_', '-').startsWith(LANG));
  const score = (v: SpeechSynthesisVoice) => (v.lang.replace('_', '-') === LN.tts ? 4 : 0) + (/google|natural|neural|premium|enhanced|siri/i.test(v.name) ? 2 : 0) + (v.localService ? 0 : 1);
  return m.sort((a, b) => score(b) - score(a))[0] ?? null;
}

/** Ovoz topilmaganda chaqiriladi — UI bir marta ko'rsatma oynasini ochadi. */
export let onNoVoice: (() => void) | null = null;
export const setNoVoiceHandler = (f: () => void) => { onNoVoice = f; };
let warned = false;

function say(text: string, rate: number) {
  try {
    if (voice === null || voice === undefined) voice = pickVoice();
    // Ovozlar ro'yxati hali kelmagan (undefined) bo'lsa ham DARHOL o'qiymiz — faqat `lang` bilan.
    // setTimeout bilan kutish bosish (user gesture) kontekstini yo'qotadi: iOS/Android WebView'da ovoz chiqmaydi
    // (birinchi so'zda kolonka ishlamasligining sababi shu edi). Rus ovozi yo'qligi aniq ma'lum bo'lsagina to'xtaymiz.
    if (voice === null && LANG !== 'en') { if (!warned) { warned = true; onNoVoice?.(); } return; }
    const u = new SpeechSynthesisUtterance(text);
    u.lang = voice ? voice.lang : LN.tts; u.rate = rate; if (voice) u.voice = voice;
    // cancel() dan keyin darhol speak() ba'zi Android'larda yangi gapni yutib yuboradi — faqat kerak bo'lsa to'xtatamiz
    if (speechSynthesis.speaking || speechSynthesis.pending) speechSynthesis.cancel();
    speechSynthesis.resume(); speechSynthesis.speak(u);
  } catch { /* ignore */ }
}
/* ---------- server audio ---------- */
export const audioUrl = (id: number, k: 'w' | 'ex' = 'w') => `${API_URL}/v1/words/${id}/audio${k === 'ex' ? '?k=ex' : ''}`;
let idx: { n: number; m: Map<string, string> } | null = null;
/** Matn → audio URL (bazadagi so'z yoki misol bo'lsa). */
function urlFor(text: string): string | null {
  if (!idx || idx.n !== W.length) {
    const m = new Map<string, string>();
    for (const w of W) { if (!m.has(w.w)) m.set(w.w, audioUrl(w.id)); if (w.ex && !m.has(w.ex)) m.set(w.ex, audioUrl(w.id, 'ex')); }
    idx = { n: W.length, m };
  }
  return idx.m.get(text) ?? null;
}
/** Server ketma-ket ishlamasa — sessiya oxirigacha faqat speechSynthesis. */
let audioFails = 0;
// Bitta <audio> elementi qayta ishlatiladi: iOS'da birinchi bosishda «ochilgach», keyingi avtomatik o'qishlar ham ruxsat etiladi
let el: HTMLAudioElement | null = null;
function play(url: string, rate: number, fallback: () => void) {
  try {
    if (!el) { el = new Audio(); el.preload = 'auto'; }
    const a = el;
    try { speechSynthesis.cancel(); } catch { /* ignore */ }
    a.pause(); a.src = url; a.playbackRate = rate;
    (a as HTMLAudioElement & { preservesPitch?: boolean }).preservesPitch = true;
    let failed = false;
    const fail = () => { if (failed) return; failed = true; if (++audioFails >= 3) console.warn('[tts] server audio off'); fallback(); };
    a.onerror = fail;
    a.onplaying = () => { audioFails = 0; };
    a.play().catch((e: unknown) => { if ((e as { name?: string })?.name !== 'AbortError') fail(); });
  } catch { fallback(); }
}
function out(text: string, rate: number, ttsRate: number) {
  const url = audioFails < 3 ? urlFor(text) : null;
  if (url) play(url, rate, () => say(text, ttsRate)); else say(text, ttsRate);
}
export const speak = (text: string) => out(text, 1, 0.9);
export const speakSlow = (text: string) => out(text, 0.65, 0.5);
/** Dars so'zlarining ovozini oldindan tayyorlash (server keshi + brauzer keshi) — birinchi bosishda kutilmasin. */
export function prefetchAudio(ids: number[]) {
  for (const id of ids) if (BYID[id]) fetch(audioUrl(id)).catch(() => {});
}
/** Ovozni toʻxtatish (oʻyindan chiqqanda). */
export const hush = () => { try { el?.pause(); } catch { /* ignore */ } try { speechSynthesis.cancel(); } catch { /* ignore */ } };

/**
 * Sonni oʻrganilayotgan tilda aytish (Sonlar oʻyini). `onEnd` — ovoz tugaganda yoki kutish muddati oʻtganda
 * (ovoz boʻlmasa ham) bir marta chaqiriladi: taymer ovoz tugashini kutmasdan ham boshlanadi.
 */
export function sayNumber(n: number, onEnd?: () => void) {
  let done = false;
  const fin = () => { if (done) return; done = true; onEnd?.(); };
  try {
    if (voice === null || voice === undefined) voice = pickVoice();
    const v = voice ?? null;
    if (!v && LANG !== 'en') { if (!warned) { warned = true; onNoVoice?.(); } }
    else {
      const u = new SpeechSynthesisUtterance(String(n));
      u.lang = v ? v.lang : LN.tts; u.rate = 0.95; if (v) u.voice = v;
      u.onend = fin; u.onerror = fin;
      if (speechSynthesis.speaking || speechSynthesis.pending) speechSynthesis.cancel(); speechSynthesis.resume(); speechSynthesis.speak(u);
    }
  } catch { /* ignore */ }
  setTimeout(fin, 900 + String(n).length * 450);
}
try { speechSynthesis.onvoiceschanged = () => { voice = pickVoice(); }; speechSynthesis.getVoices(); } catch { /* ignore */ }

/* ---------- mikrofon ---------- */
/** Talaffuz tekshiruvi ishlaydimi (sessiya davomida): null — sinalmagan, false — mikrofon/server ishlamadi. */
export let MIC_OK: boolean | null = null;
export const setMicOk = (v: boolean | null) => { MIC_OK = v; };
